import {
	BusyIndicator,
	DateString,
	PipeSeparator
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import {
	InterestedMagisterParty,
	MagisterLiveInformationProvider
} from "./provider";
import { ListItem } from "../ListItem";
import {
	GradesResponseModel,
	MagisterGrade,
	ResultStatus
} from "client/Api/Workspace/LiveInformation/Magister/magisterLiveInformation";
import { DestinationWindow } from "client/Api/Apps/common";
import { isNotNil } from "client/Misc/utils";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { TimeZone } from "client/DateAndTime/timezones";
import { getStartOfDay } from "client/DateAndTime/dateTimeUtils";

export type GradesProps = {
	tabId: Guid;
	connectionId: number;
	provider: MagisterLiveInformationProvider;
	destinationWindow: DestinationWindow;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

export const Grades = (props: GradesProps): ReactElement => {
	const {
		tabId,
		connectionId,
		provider,
		destinationWindow,
		timezone,
		addAnalyticsEvent
	} = props;

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	useEffect(
		(): (() => void) => {
			const party: InterestedMagisterParty = {
				partyId: tabId,
				resourceId: {
					connectionId,
					date: getStartOfDay(new Date(), timezone)
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateGrades: (response: GradesResponseModel, isTeacher: boolean | null): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						response,
						isTeacher
					}));
				}
			};

			provider.register(party, { grades: true });

			return () => provider.unregister(party);
		},
		[
			connectionId,
			provider,
			tabId,
			timezone
		]
	);

	const navigationHandler = useCallback(
		(): void => {
			addAnalyticsEvent();

			navigateRelativeToEnvironment(
				`/Workspace/LiveInformation/Magister/${connectionId}/GoToGrades`,
				destinationWindow === DestinationWindow.NewWindow
			);
		},
		[
			addAnalyticsEvent,
			connectionId,
			destinationWindow
		]
	);

	const refreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const renderContent = (): ReactElement | null => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={refreshHandler} />;
		}

		if (loadingState.hasGlobalFailure || (loadingState.response.status !== ResultStatus.Success)) {
			return (
				<LiveTileContent.NoResults
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					trackingId="has-error"
				/>
			);
		}

		const {
			response
		} = loadingState;

		if (response.items.length === 0) {
			return (
				<LiveTileContent.NoResults
					icon="list"
					title={localize("_NoItemsToShow")}
					message={localize("_ThereAreCurrentlyNoItemsToShowHere")}
					trackingId="no-items"
				/>
			);
		}

		return (
			<LiveTileContent.Scrollable>
				<LiveTileContent.List>
					{response.items.map((x) => (
						<GradeListItem
							key={x.id}
							grade={x}
							onNavigate={navigationHandler}
							timezone={timezone}
						/>
					))}
				</LiveTileContent.List>
			</LiveTileContent.Scrollable>
		);
	};

	return (
		<>
			<LiveTileContent.Content>
				<LiveTileContent.ContentStack>
					<LiveTileContent.BusyIndicatorContainer>
						{renderContent()}
					</LiveTileContent.BusyIndicatorContainer>
				</LiveTileContent.ContentStack>
			</LiveTileContent.Content>

			<LiveTileContent.Footer
				name={localize("_MyGrades")}
				onClick={navigationHandler}
			/>
		</>
	);
};
Grades.displayName = "Grades";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: false;
			hasGlobalFailure: false;
			isTeacher: boolean | null;
			response: GradesResponseModel;
		}
	);

export type GradeListItemProps = {
	grade: MagisterGrade;
	onNavigate: () => void;
	timezone: TimeZone;
};

export const GradeListItem = (props: GradeListItemProps): ReactElement => {
	const {
		grade,
		onNavigate,
		timezone
	} = props;

	return (
		<ListItem
			title={grade.description ?? undefined}
			isHoverable
			onClick={onNavigate}
		>
			<ListItem.Content>
				<ListItem.Row>
					<ListItem.Title>{grade.className}</ListItem.Title>
					<ListItem.RightAddition>
						<ListItem.Title>{grade.valueText ?? grade.value}</ListItem.Title>
					</ListItem.RightAddition>
				</ListItem.Row>

				<ListItem.Row>
					<ListItem.Info>
						{grade.description}

						{(grade.description && grade.date) && (
							<PipeSeparator />
						)}

						{grade.date && (
							<DateString
								value={grade.date}
								format="DM(Y)"
								timezone={timezone}
							/>
						)}
					</ListItem.Info>
					{isNotNil(grade.weightingFactor) && (
						<ListItem.RightAddition>
							<ListItem.Info>{grade.weightingFactor}x</ListItem.Info>
						</ListItem.RightAddition>
					)}
				</ListItem.Row>
			</ListItem.Content>
		</ListItem>
	);
};
GradeListItem.displayName = "GradeListItem";
